import mixpanel from 'mixpanel-browser'

/**
 * Wrapper central do Mixpanel.
 *
 * Toda chamada vira no-op se o token não estiver definido
 * (mesmo padrão do react-hotjar/GTM, guardado por env var). Isso permite
 * rodar localmente/QA sem token sem quebrar nada, e centraliza o SDK para
 * facilitar uma eventual troca de ferramenta de analytics.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * Dois comportamentos INDEPENDENTES:
 *
 * 1. ENVIAR ao Mixpanel  →  apenas em PRODUÇÃO, com token e SDK inicializado.
 *    Desenvolvimento e QA (qa.education) NUNCA enviam eventos.
 *
 * 2. LOGAR no console (modo simulação) → apenas quando você liga a flag global
 *    `window.__MIXPANEL_SIM__ = true` (pode setar no console do navegador a
 *    qualquer momento, sem reload). Útil em dev/QA para inspecionar os eventos
 *    sem enviá-los. `window.__MIXPANEL_SIM__ = false` (ou não setar) desliga.
 * ──────────────────────────────────────────────────────────────────────────
 */

// Flag global para LIGAR o log de simulação no console, sob demanda.
// Ex. no console do navegador: `window.__MIXPANEL_SIM__ = true`
declare global {
  interface Window {
    __MIXPANEL_SIM__?: boolean
  }
}

let inicializado = false

// LOG no console: só quando a flag global está explicitamente ligada.
const deveLogar = (): boolean =>
  typeof window !== 'undefined' && window.__MIXPANEL_SIM__ === true

// ENVIAR de verdade: só em produção, com SDK inicializado (token presente).
// Dev/QA nunca enviam, independentemente da flag de log.
const ehProducao = (): boolean => {
  const ehDesenvolvimento = process.env.NODE_ENV !== 'production'
  const ehQa =
    typeof window !== 'undefined' &&
    window.location.hostname.includes('qa.education')
  return !ehDesenvolvimento && !ehQa
}

const deveEnviar = (): boolean => inicializado && ehProducao()

// Espelho local das super properties só para enriquecer os logs de simulação.
const superPropriedadesSimuladas: Record<string, unknown> = {}

const logSimulado = (titulo: string, dados?: Record<string, unknown>): void => {
  // eslint-disable-next-line no-console
  console.log(
    `%c[Mixpanel · simulação]%c ${titulo}`,
    'background:#7856ff;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
    'color:#7856ff;font-weight:bold',
    dados ?? ''
  )
}

export const inicializarMixpanel = (): void => {
  const token = process.env.REACT_APP_MIXPANEL_TOKEN
  // Só inicializa o SDK real em produção com token. Dev/QA nunca enviam.
  if (!token || inicializado || !ehProducao()) return

  mixpanel.init(token, {
    // Captura automática de UTM/referrer e demais default properties.
    // debug ligado apenas fora de produção para inspecionar eventos no console.
    debug: process.env.NODE_ENV !== 'production',
    ignore_dnt: false,
    persistence: 'localStorage'
  })
  inicializado = true
}

export const track = (
  evento: string,
  propriedades?: Record<string, unknown>
): void => {
  if (deveLogar()) {
    logSimulado(`track → ${evento}`, {
      ...superPropriedadesSimuladas,
      ...propriedades
    })
  }
  if (deveEnviar()) mixpanel.track(evento, propriedades)
}

export const registrarSuperPropriedades = (
  propriedades: Record<string, unknown>
): void => {
  // Mantém o espelho atualizado para o log; barato e sempre coerente.
  Object.assign(superPropriedadesSimuladas, propriedades)
  if (deveLogar()) logSimulado('register (super properties)', propriedades)
  if (deveEnviar()) mixpanel.register(propriedades)
}

export const limparSuperPropriedades = (chaves: string[]): void => {
  chaves.forEach(chave => delete superPropriedadesSimuladas[chave])
  if (deveLogar()) logSimulado('unregister (super properties)', { chaves })
  if (deveEnviar()) chaves.forEach(chave => mixpanel.unregister(chave))
}

export const identificar = (idUsuario: string): void => {
  if (!idUsuario) return
  if (deveLogar()) logSimulado('identify', { distinct_id: idUsuario })
  if (deveEnviar()) mixpanel.identify(idUsuario)
}

export const iniciarCronometro = (evento: string): void => {
  if (deveLogar()) logSimulado(`time_event (cronômetro) → ${evento}`)
  if (deveEnviar()) mixpanel.time_event(evento)
}
